import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Button, Divider, Space, Input, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { BASE_PATH_SERVER } from '../../api/config';
import { getAccessTokenApi } from '../../api/auth';

// Select de ubicaciones (ascensores + areas genericas) por cliente con creacion
// in-line. Si el usuario escribe un nombre que no existe en la lista del
// cliente, aparece un boton "Crear <X>" que hace POST /ascensor/add con solo
// {nombre, id_cliente} (el resto de campos son nullable). Util para tareas de
// ODT donde la ubicacion puede ser "Sala de maquinas", "Acceso", etc., no solo
// ascensores literales.

function AscensorInlineSelect({
  value,
  onChange,
  idClient,
  size = 'small',
  placeholder,
  mode,
  onListLoaded,
}) {
  const isMultiple = mode === 'multiple';
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchAscensores = async () => {
    if (!idClient) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_PATH_SERVER}clientes/${idClient}/ascensores`, {
        headers: { Authorization: getAccessTokenApi() },
      });
      const data = await res.json();
      if (!mountedRef.current) return;
      const raw = Array.isArray(data)
        ? data
        : Array.isArray(data?.ascensores)
        ? data.ascensores
        : [];
      const lista = raw.map((a) => ({ nombre: a.nombre, id: a.id }));
      setOptions(lista.map((a) => ({ value: a.nombre, label: a.nombre, id: a.id })));
      onListLoaded?.(lista);
    } catch {
      // silencioso: el Select queda vacio, el usuario puede crear
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAscensores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idClient]);

  const normalizedSearch = search.trim();
  const hasExactMatch = useMemo(
    () => options.some((o) => o.value.toLowerCase() === normalizedSearch.toLowerCase()),
    [options, normalizedSearch]
  );

  // Si el/los value(s) actuales no estan en options (ej. texto libre heredado
  // de flujo legacy), los agregamos como "options virtuales" para que el
  // Select los muestre sin perder el dato.
  const effectiveOptions = useMemo(() => {
    if (!value) return options;
    const current = isMultiple ? (Array.isArray(value) ? value : []) : [value];
    const missing = current.filter((v) => v && !options.some((o) => o.value === v));
    if (missing.length === 0) return options;
    return [
      ...options,
      ...missing.map((v) => ({ value: v, label: `${v} (libre)`, virtual: true })),
    ];
  }, [options, value, isMultiple]);

  const handleCreate = async () => {
    if (!normalizedSearch || !idClient) return;
    // La coma es el separador del CSV que persiste budgetitem.elevator /
    // order_item.elevator. Si se permite en el nombre, el split posterior
    // rompe. Validamos en el cliente; el server no bloquea por esto.
    if (normalizedSearch.includes(',')) {
      message.warning('El nombre no puede contener comas — usá otro separador');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${BASE_PATH_SERVER}ascensor/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAccessTokenApi(),
        },
        // requiere_plan=false: ubicaciones creadas in-line desde el editor de
        // ODT no generan plan de mantencion ni alertas. El admin puede
        // promoverlas a ascensor "real" desde la gestion de ascensores.
        // tipo_equipo_categoria='sin_definir': son ubicaciones genéricas
        // (Sala de máquinas, Acceso, etc.), no ascensores reales — no
        // queremos que queden categorizadas como pasajeros por el default DB.
        body: JSON.stringify({
          nombre: normalizedSearch,
          id_cliente: idClient,
          requiere_plan: false,
          tipo_equipo_categoria: 'sin_definir',
        }),
      });
      const data = await res.json();
      if (res.status === 409) {
        // Duplicado: ya existe un ascensor con ese nombre para el cliente.
        // Refrescamos options (pudo haber llegado por otra pestaña/race).
        message.warning(data?.message || `Ya existe una ubicación "${normalizedSearch}"`);
        await fetchAscensores();
        return;
      }
      if (data?.state === 'success') {
        message.success(`Ubicacion "${normalizedSearch}" creada`);
        await fetchAscensores();
        if (isMultiple) {
          const current = Array.isArray(value) ? value : [];
          onChange?.([...current, normalizedSearch]);
        } else {
          onChange?.(normalizedSearch);
        }
        setSearch('');
      } else {
        message.error(data?.message || 'No se pudo crear la ubicacion');
      }
    } catch (err) {
      message.error(err?.message || 'Error al crear la ubicacion');
    } finally {
      if (mountedRef.current) setCreating(false);
    }
  };

  return (
    <Select
      mode={isMultiple ? 'multiple' : undefined}
      value={isMultiple ? value || [] : value || undefined}
      onChange={onChange}
      size={size}
      showSearch
      allowClear={!isMultiple}
      loading={loading}
      placeholder={placeholder || 'Ascensor o ubicación'}
      optionFilterProp="label"
      searchValue={search}
      onSearch={setSearch}
      options={effectiveOptions}
      maxTagCount="responsive"
      notFoundContent={
        normalizedSearch ? (
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            loading={creating}
            block
            style={{ textAlign: 'left' }}
          >
            Crear &quot;{normalizedSearch}&quot;
          </Button>
        ) : (
          'Sin resultados'
        )
      }
      popupRender={(menu) => (
        <>
          {menu}
          {normalizedSearch && !hasExactMatch ? (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <Space style={{ padding: '4px 8px' }}>
                <Input size="small" value={normalizedSearch} readOnly style={{ width: 160 }} />
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={handleCreate}
                  loading={creating}
                >
                  Crear
                </Button>
              </Space>
            </>
          ) : null}
        </>
      )}
    />
  );
}

export default AscensorInlineSelect;
